import { useMutation, useQueries, useQuery } from '@tanstack/react-query';

import competencyGroupService from './competencyGroupService';
import competencyProfileService from './competencyProfileService';
import competencyService from './competencyService';
import proficiencyLevelService from './proficiencyLevelService';

import type { Competency } from '@/types/competencies/Competency';
import type { CompetencyGroup } from '@/types/competencies/CompetencyGroup';
import type {
    CompetencyProfileCreateRequest,
    CompetencyProfileUpdateRequest,
    CompetencyProfileListResponse,
    CompetencyProfileResponse,
} from '@/types/competencies/CompetencyProfileApi';
import type { ProficiencyLevel } from '@/types/competencies/ProficiencyLevel';

import { queryClient } from '@/AppWithProviders';
import {
    CompetenciesQueryKey,
    CompetencyGroupMutationKey,
    CompetencyGroupQueryKey,
    CompetencyMutationKey,
    CompetencyProfileMutationKey,
    CompetencyProfileQueryKey,
    CompetencyProfileAssignmentQueryKey,
    CompetencyQueryKey,
} from '@/constants/QueryKeys';

interface UseCompetencyProfilesByIdsParams {
    profileIds: string[];
    enabled?: boolean;
}

export const useCompetencyProfiles = (options?: { enabled?: boolean }) => {
    return useQuery<CompetencyProfileListResponse[], Error>({
        queryKey: [CompetencyProfileQueryKey.PROFILES],
        queryFn: competencyProfileService.getCompetencyProfiles,
        enabled: options?.enabled,
    });
};

export const useCompetencyProfilesByIds = ({
    profileIds,
    enabled = true,
}: UseCompetencyProfilesByIdsParams) => {
    return useQuery<CompetencyProfileResponse[], Error>({
        queryKey: [CompetencyProfileQueryKey.PROFILE, profileIds],
        queryFn: () =>
            Promise.all(
                profileIds.map((profileId) =>
                    competencyProfileService.getCompetencyProfileById(profileId),
                ),
            ),
        enabled: enabled && profileIds.length > 0,
    });
};

export const useAddCompetenciesToProfile = () => {
    return useMutation<
        CompetencyProfileResponse,
        Error,
        { profileId: string; competencyIds: string[] }
    >({
        mutationKey: [CompetencyProfileMutationKey.ADD_COMPETENCIES],
        mutationFn: ({ profileId, competencyIds }) =>
            competencyProfileService.addCompetenciesToProfile(profileId, { competencyIds }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [CompetencyProfileQueryKey.PROFILE],
            });
            queryClient.invalidateQueries({
                queryKey: [CompetencyProfileQueryKey.PROFILES],
            });
        },
    });
};

export const useCreateCompetencyProfile = () => {
    return useMutation<CompetencyProfileResponse, Error, CompetencyProfileCreateRequest>({
        mutationKey: [CompetencyProfileMutationKey.CREATE_PROFILE],
        mutationFn: (request) => competencyProfileService.createCompetencyProfile(request),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [CompetencyProfileQueryKey.PROFILE],
            });
            queryClient.invalidateQueries({
                queryKey: [CompetencyProfileQueryKey.PROFILES],
            });
            queryClient.invalidateQueries({
                queryKey: [CompetencyProfileAssignmentQueryKey.PROFILES],
            });
        },
    });
};

export const useUpdateCompetencyProfile = () => {
    return useMutation<
        CompetencyProfileResponse,
        Error,
        { profileId: string; request: CompetencyProfileUpdateRequest }
    >({
        mutationKey: [CompetencyProfileMutationKey.UPDATE_PROFILE],
        mutationFn: ({ profileId, request }) =>
            competencyProfileService.updateCompetencyProfile(profileId, request),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [CompetencyProfileQueryKey.PROFILE],
            });
            queryClient.invalidateQueries({
                queryKey: [CompetencyProfileQueryKey.PROFILES],
            });
            queryClient.invalidateQueries({
                queryKey: [CompetencyProfileAssignmentQueryKey.PROFILES],
            });
        },
    });
};

export const useDeleteCompetencyProfile = () => {
    return useMutation<CompetencyProfileResponse, Error, string>({
        mutationKey: [CompetencyProfileMutationKey.DELETE_PROFILE],
        mutationFn: (profileId) => competencyProfileService.deleteCompetencyProfile(profileId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [CompetencyProfileQueryKey.PROFILE],
            });
            queryClient.invalidateQueries({
                queryKey: [CompetencyProfileQueryKey.PROFILES],
            });
            queryClient.invalidateQueries({
                queryKey: [CompetencyProfileAssignmentQueryKey.PROFILES],
            });
            queryClient.invalidateQueries({
                queryKey: [CompetencyProfileAssignmentQueryKey.ASSIGNMENTS],
            });
        },
    });
};

export const useRemoveCompetencyFromProfile = () => {
    return useMutation<
        CompetencyProfileResponse,
        Error,
        { profileId: string; competencyId: string }
    >({
        mutationKey: [CompetencyProfileMutationKey.REMOVE_COMPETENCY],
        mutationFn: ({ profileId, competencyId }) =>
            competencyProfileService.removeCompetencyFromProfile(profileId, competencyId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [CompetencyProfileQueryKey.PROFILE],
            });
            queryClient.invalidateQueries({
                queryKey: [CompetencyProfileQueryKey.PROFILES],
            });
        },
    });
};

// A single targeted call for an employee's assigned profile (with competencies).
// Returns null when the employee has no active assignment.
export const useCompetencyProfileByEmployee = (employeeId?: string) => {
    return useQuery<CompetencyProfileResponse | null, Error>({
        queryKey: [CompetencyProfileQueryKey.PROFILE, 'by-employee', employeeId],
        queryFn: () => competencyProfileService.getCompetencyProfileByEmployee(employeeId!),
        enabled: !!employeeId,
    });
};

export const useCompetencyProfilesByEmployees = (employeeIds: string[]) => {
    return useQueries({
        queries: employeeIds.map((employeeId) => ({
            queryKey: [CompetencyProfileQueryKey.PROFILE, 'by-employee', employeeId],
            queryFn: () => competencyProfileService.getCompetencyProfileByEmployee(employeeId),
            enabled: !!employeeId,
        })),
    });
};

export const useGetCompetencyGroups = (options?: {
    page?: number;
    pageSize?: number;
    enabled?: boolean;
}) => {
    const page = options?.page ?? 1;
    const pageSize = options?.pageSize ?? 50;
    return useQuery<CompetencyGroup[]>({
        queryKey: [CompetencyGroupQueryKey.COMPETENCY_GROUPS, page, pageSize],
        queryFn: async () => {
            const paginated = await competencyGroupService.getCompetencyGroups(page, pageSize);
            return paginated.items;
        },
        enabled: options?.enabled ?? true,
    });
};

export const useGetCompetencyGroupById = (id: string) => {
    return useQuery<CompetencyGroup>({
        queryKey: [CompetencyGroupQueryKey.COMPETENCY_GROUP, id],
        queryFn: () => competencyGroupService.getCompetencyGroupById(id),
        enabled: !!id,
    });
};

export const useCreateCompetencyGroup = () => {
    return useMutation<CompetencyGroup, Error, Omit<CompetencyGroup, 'id' | 'competencies'>>({
        mutationKey: [CompetencyGroupMutationKey.CREATE_COMPETENCY_GROUP],
        mutationFn: (competencyGroup) =>
            competencyGroupService.createCompetencyGroup(competencyGroup),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [CompetencyGroupQueryKey.COMPETENCY_GROUPS],
            });
        },
    });
};

export const useUpdateCompetencyGroup = () => {
    return useMutation<CompetencyGroup, Error, CompetencyGroup>({
        mutationKey: [CompetencyGroupMutationKey.UPDATE_COMPETENCY_GROUP],
        mutationFn: (competencyGroup: CompetencyGroup) =>
            competencyGroupService.updateCompetencyGroup(competencyGroup),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [CompetencyGroupQueryKey.COMPETENCY_GROUPS],
            });
            queryClient.invalidateQueries({
                queryKey: [CompetencyGroupQueryKey.COMPETENCY_GROUP],
            });
        },
    });
};

export const useDeleteCompetencyGroup = () => {
    return useMutation<void, Error, string>({
        mutationKey: [CompetencyGroupMutationKey.DELETE_COMPETENCY_GROUP],
        mutationFn: (id: string) => competencyGroupService.deleteCompetencyGroup(id),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [CompetencyGroupQueryKey.COMPETENCY_GROUPS],
            });
        },
    });
};

export const useGetProficiencyLevels = () => {
    return useQuery({
        queryKey: [CompetenciesQueryKey.PROFICIENCY_LEVELS],
        queryFn: proficiencyLevelService.getProficiencyLevels,
    });
};

export const useUpdateProficiencyLevels = () => {
    return useMutation<ProficiencyLevel[], Error, ProficiencyLevel[]>({
        mutationFn: (levels: ProficiencyLevel[]) =>
            proficiencyLevelService.updateProficiencyLevels(levels),
        onSuccess: (data) => {
            queryClient.setQueryData([CompetenciesQueryKey.PROFICIENCY_LEVELS], data);
        },
    });
};

export const useGetCompetencies = (options?: { enabled?: boolean }) => {
    return useQuery<Competency[]>({
        queryKey: [CompetencyQueryKey.COMPETENCIES],
        queryFn: competencyService.getCompetencyList,
        enabled: options?.enabled,
    });
};

export const useGetCompetencyById = (id: string) => {
    return useQuery<Competency>({
        queryKey: [CompetencyQueryKey.COMPETENCY, id],
        queryFn: () => competencyService.getCompetencyById(id),
        enabled: !!id,
    });
};

export const useCreateCompetency = () => {
    return useMutation<Competency, Error, Omit<Competency, 'id'>>({
        mutationKey: [CompetencyMutationKey.CREATE_COMPETENCY],
        mutationFn: (competency) => competencyService.createCompetency(competency),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [CompetencyQueryKey.COMPETENCIES],
            });
            queryClient.invalidateQueries({
                queryKey: [CompetencyGroupQueryKey.COMPETENCY_GROUPS],
            });
        },
    });
};

export const useUpdateCompetency = () => {
    return useMutation<Competency, Error, Competency>({
        mutationKey: [CompetencyMutationKey.UPDATE_COMPETENCY],
        mutationFn: (competency) => competencyService.updateCompetency(competency),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [CompetencyQueryKey.COMPETENCIES],
            });
            queryClient.invalidateQueries({
                queryKey: [CompetencyQueryKey.COMPETENCY],
            });
            queryClient.invalidateQueries({
                queryKey: [CompetencyGroupQueryKey.COMPETENCY_GROUPS],
            });
        },
    });
};

export const useDeleteCompetency = () => {
    return useMutation<Competency, Error, string>({
        mutationKey: [CompetencyMutationKey.DELETE_COMPETENCY],
        mutationFn: (id) => competencyService.deleteCompetency(id),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [CompetencyQueryKey.COMPETENCIES],
            });
            queryClient.invalidateQueries({
                queryKey: [CompetencyGroupQueryKey.COMPETENCY_GROUPS],
            });
        },
    });
};

export const useGetCompetencyImpactCounts = (id: string) => {
    return useQuery({
        queryKey: [CompetencyQueryKey.COMPETENCY_IMPACT, id],
        queryFn: () => competencyService.getCompetencyImpactCounts(id),
        enabled: false,
    });
};
